import { useId, useState } from 'react'
import { usePage } from '@inertiajs/react'
import { DateTimePickerInput } from '@/shared/ui/datetime-picker-input'
import { Button } from '@/shared/ui/button'
import { Label } from '@/shared/ui/label'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'
import { t } from '@/shared/lib/i18n'
import { sanitizeRichTextHtml, richTextToPlainText } from '@/shared/lib/rich-text-html'
import { formatRecordedInstantWithZone } from '@/shared/lib/tenantDateTime'
import { cn } from '@/shared/lib/utils'
import type WebInstrumentsTaskLogDataField from '@/shared/types/web/WebInstrumentsTaskLogDataField'
import {
  DataFieldInputs,
  LINEAR_ACCEPTABLE,
  type DataFieldEntry,
  type DataFieldFile,
} from './DataFieldInputs'

export type { DataFieldEntry, DataFieldFile }

const buildInitialEntries = (dataFields: WebInstrumentsTaskLogDataField[]): DataFieldEntry[] =>
  dataFields.map((field) => ({
    instrumentTaskDataFieldId: field.id,
    value: '',
    linearAcceptable: '',
  }))

interface ResolveFormProps {
  /** ISO date (YYYY-MM-DD) of the missed occurrence; fallback for the date/time picker when missedTime is absent. */
  date: string
  /** UTC iso8601 of the overdue boundary — converted to site-local wall clock for the date/time picker prefill. */
  missedTime?: string | null
  /** Site-local wall clock (YYYY-MM-DDTHH:mm) of the window open instant (window_start = occurrence − lead).
   *  Feeds the lower-bound submit guard. Absent for migrated legacy rows; guard is skipped when nil. */
  windowStartTime?: string | null
  /** IANA timezone of the instrument's site; controls TZ-correct submission and "now" in the picker. */
  siteTimeZone?: string | null
  taskNotes?: string | null
  /** When the surrounding dialog already renders the task notes (calendar day view), suppress
      the in-form notes block to avoid a duplicate (REBUILD-377). */
  hideTaskNotes?: boolean
  /** Configured data fields for this task (empty array = no-field path, unchanged behaviour). */
  dataFields?: WebInstrumentsTaskLogDataField[]
  onSubmit: (
    completedAt: string,
    comments: string,
    dataFieldEntries: DataFieldEntry[],
    dataFieldFiles: DataFieldFile[],
  ) => void
  onCancel: () => void
  processing: boolean
  /** Server-side completedAt refusal (e.g. offline-period guard). Shown while the dialog stays open. */
  serverCompletedAtError?: string | null
}

/**
 * Retroactive "fill the hole" form shared by the non-compliance dashboard and the
 * instrument calendar. Captures completed-at, reason, and (when the task has data
 * fields) per-field measurement values — then posts them to the resolve_log endpoint.
 * Renders body only so it works inside a Dialog or inline.
 */
export const ResolveForm = ({
  date,
  missedTime,
  windowStartTime,
  siteTimeZone,
  taskNotes,
  hideTaskNotes = false,
  dataFields = [],
  onSubmit,
  onCancel,
  processing,
  serverCompletedAtError = null,
}: ResolveFormProps) => {
  const { currentUserInfo, tenantTimeFormat } = usePage<{
    currentUserInfo: { displayName: string } | null
    tenantTimeFormat?: number | null
  }>().props
  const effectiveTimeZone = siteTimeZone || undefined
  const defaultCompletedAt = missedTime || `${date.split('T')[0]}T00:00`
  const completedAtId = useId()
  const commentsId = useId()
  const [completedAt, setCompletedAt] = useState(defaultCompletedAt)
  const [completedAtError, setCompletedAtError] = useState<string | null>(null)
  const displayedCompletedAtError = completedAtError || serverCompletedAtError || null
  const [comments, setComments] = useState('')
  const [touched, setTouched] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const [dataFieldEntries, setDataFieldEntries] = useState<DataFieldEntry[]>(() =>
    buildInitialEntries(dataFields),
  )
  const [dataFieldFiles, setDataFieldFiles] = useState<Record<number, DataFieldFile | null>>({})
  const [dataFieldsValid, setDataFieldsValid] = useState(true)

  const commentsTextLength = comments.replace(/<[^>]*>/g, '').length
  const commentsError = touched && commentsTextLength === 0

  const updateDataFieldValue = (fieldId: number, value: string) => {
    setDataFieldEntries((prev) =>
      prev.map((row) => (row.instrumentTaskDataFieldId === fieldId ? { ...row, value } : row)),
    )
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next[`dataField${fieldId}`]
      return next
    })
  }

  const updateLinearAcceptable = (fieldId: number, choice: string) => {
    setDataFieldEntries((prev) =>
      prev.map((row) =>
        row.instrumentTaskDataFieldId === fieldId
          ? {
              ...row,
              linearAcceptable: choice,
              value: choice === LINEAR_ACCEPTABLE ? '' : row.value,
            }
          : row,
      ),
    )
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next[`dataField${fieldId}`]
      return next
    })
  }

  const handleFileSelected = (fieldId: number, file: File | null) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result)
      const dataBase64 = result.includes(',') ? result.split(',')[1] : result
      setDataFieldFiles((prev) => ({
        ...prev,
        [fieldId]: {
          instrumentTaskDataFieldId: fieldId,
          attachmentName: file.name,
          mimeType: file.type,
          dataBase64,
        },
      }))
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next[`dataField${fieldId}`]
        return next
      })
    }
    reader.readAsDataURL(file)
  }

  const handleFileRemoved = (fieldId: number) => {
    setDataFieldFiles((prev) => ({ ...prev, [fieldId]: null }))
  }

  const validateRequiredFields = (): boolean => {
    const errors: Record<string, string> = {}
    for (const field of dataFields) {
      if (!field.required) continue

      if (field.dataFieldType === 'fileAttachment') {
        if (!dataFieldFiles[field.id]) {
          errors[`dataField${field.id}`] = t('instruments.taskLogs.fieldRequired')
        }
        continue
      }

      const entry = dataFieldEntries.find((e) => e.instrumentTaskDataFieldId === field.id)
      const satisfied =
        field.dataFieldType === 'linear'
          ? Boolean(entry?.linearAcceptable)
          : Boolean(entry?.value?.trim())

      if (!satisfied) {
        errors[`dataField${field.id}`] = t('instruments.taskLogs.errors.required_field_invalid')
      }
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = () => {
    setTouched(true)
    if (commentsTextLength === 0) return
    if (!validateRequiredFields()) return

    const files: DataFieldFile[] = Object.values(dataFieldFiles).filter(
      (f): f is DataFieldFile => f !== null,
    )

    // E36-S3: submit the raw wall-clock string as-is. The backend resolves the record's
    // site zone and converts to UTC via SiteTimeZone.wall_clock_to_utc. (The prefilled
    // `missedTime` is already a site-local wall-clock string, so no conversion is needed.)
    //
    // JW-390: block a before-window resolve. windowStartTime is site-local wall-clock
    // (YYYY-MM-DDTHH:mm), so lexicographic comparison is chronologically correct.
    // Skipped when windowStartTime is absent (migrated legacy rows without window_start).
    if (windowStartTime && completedAt < windowStartTime) {
      setCompletedAtError(t('nonCompliance.dialogs.resolve.outOfWindowBefore'))
      return
    }
    // E38-S1 (REBUILD-307): block an out-of-window resolve before it reaches the server.
    // Both `completedAt` and `missedTime` are site-local wall-clock strings in the same
    // "YYYY-MM-DDTHH:mm" shape, so a lexicographic comparison is a correct chronological
    // comparison. The server stays the authority; this only avoids a doomed round-trip.
    if (missedTime && completedAt > missedTime) {
      setCompletedAtError(t('nonCompliance.dialogs.resolve.outOfWindow'))
      return
    }
    setCompletedAtError(null)

    onSubmit(completedAt, comments, dataFieldEntries, files)
  }

  return (
    <div className="min-w-0 space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          {t('nonCompliance.dialogs.resolve.fillTheHole')}
        </p>
        <p className="text-xs text-muted-foreground">
          {t('nonCompliance.dialogs.resolve.dateModNote')}
        </p>
      </div>

      {!hideTaskNotes && taskNotes && richTextToPlainText(taskNotes).length > 0 && (
        <div className="rounded-md border bg-muted/40 px-3 py-2">
          <p className="mb-1 text-xs font-medium text-muted-foreground">
            {t('nonCompliance.dialogs.resolve.taskNotes')}
          </p>
          {/* Task notes are rich-text HTML (REBUILD-356); render sanitized markup instead of the
              raw string so tags/quoting artefacts are not shown verbatim (REBUILD-370). */}
          <div
            className="rich-text-content prose prose-sm max-w-none text-sm break-words [&_p]:my-0"
            dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(taskNotes) }}
          />
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {t('nonCompliance.dialogs.resolve.completedBy')}
        </span>
        <span className="font-medium">{currentUserInfo?.displayName ?? '—'}</span>
      </div>

      {/* Single editor affordance (REBUILD-474 ER4): the dual-mode "display + (Edit)" toggle
          is removed. The masked DateTimePickerInput is always shown; a read-only standardized
          line above it renders the same MM/DD/YYYY, HH:mm pattern as the NC card and calendar
          (AC1/AC4) so the displayed instant is one consistent format across every surface. */}
      {/* REBUILD-466 (JW 432): legacy "Fix The Hole" order is Result (data field values) →
          Date/Time Completed → Reason. Render the data field inputs FIRST so the operator
          records what was found before why the occurrence was missed. This is a DOM reorder
          only — bindings and submit mapping (handleSubmit) are unchanged; Reason still writes
          to comments and data fields still write to dataFieldEntries. */}
      {dataFields.length > 0 && (
        <DataFieldInputs
          dataFields={dataFields}
          entries={dataFieldEntries}
          errors={fieldErrors}
          onUpdateValue={updateDataFieldValue}
          onUpdateLinearAcceptable={updateLinearAcceptable}
          onValidityChange={setDataFieldsValid}
          dataFieldFiles={dataFieldFiles}
          onFileSelected={handleFileSelected}
          onFileRemoved={handleFileRemoved}
        />
      )}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {t('nonCompliance.dialogs.resolve.completedAt')}:
          </span>
          <span className="font-medium">
            {formatRecordedInstantWithZone(completedAt, null, siteTimeZone, tenantTimeFormat)}
          </span>
        </div>
        <Label htmlFor={completedAtId} className="sr-only">
          {t('nonCompliance.dialogs.resolve.completedAt')}
        </Label>
        <DateTimePickerInput
          id={completedAtId}
          value={completedAt}
          onChange={(next) => {
            setCompletedAt(next)
            setCompletedAtError(null)
          }}
          maxDate={new Date()}
          timeZone={effectiveTimeZone}
          inputClassName={cn(displayedCompletedAtError && 'border-destructive')}
          aria-invalid={Boolean(displayedCompletedAtError)}
          showNowButton={false}
        />
        {displayedCompletedAtError && (
          <p className="text-destructive text-sm">{displayedCompletedAtError}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={commentsId}>{t('nonCompliance.dialogs.resolve.reasonLabel')}</Label>
        <RichTextEditor
          id={commentsId}
          value={comments}
          onChange={setComments}
          onBlur={() => setTouched(true)}
          placeholder={t('nonCompliance.dialogs.resolve.reasonPlaceholder')}
          className={commentsError ? 'border-destructive' : undefined}
        />
        {commentsError && (
          <p className="text-xs text-destructive">{t('nonCompliance.dialogs.commentsRequired')}</p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={processing}>
          {t('nonCompliance.dialogs.resolve.cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={processing || !dataFieldsValid}>
          {t('nonCompliance.dialogs.resolve.submit')}
        </Button>
      </div>
    </div>
  )
}
